import React, { useCallback, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { IntegrationDto, CreateIntegrationRequest, UpdateIntegrationRequest, Supplier, User, ShippingDayFormData } from '../../types';
import {
    BulatConfiguration,
    CETConfiguration,
    ElkoConfiguration,
    ExcelConfiguration,
    ITPartnerConfiguration,
    KlavtorgConfiguration,
    Logic3Configuration,
    MerlionConfiguration,
    NagConfiguration,
    NVPrintConfiguration,
    OCSConfiguration,
    ProfilineConfiguration,
    RamisConfiguration,
    ResursMediaConfiguration,
    SoftTronikConfiguration,
    VttConfiguration
} from './SupplierConfigurationComponents';

interface IntegrationEditModalProps {
    integration: IntegrationDto | null;
    currentUser: User | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: CreateIntegrationRequest | UpdateIntegrationRequest) => Promise<void>;
}

interface IntegrationJobConfiguration {
    loadFiltersBeforePull: boolean;
    filtersFilePath: string;
    exportType: string;
    exportFilePath: string;
    backupEnabled: boolean;
    backupFormat: string;
}

interface FormData {
    name: string;
    supplierName: string;
    data: string;
    jobConfiguration?: IntegrationJobConfiguration;
    selectedWarehouses?: string[];
    // Настройки доставки
    stockLifeTimeHours?: number;
    deliveryTimeHours?: number;
    saveExpectedToMainProduct?: boolean;
    isStockQuantitySource?: boolean;
    shippingDays?: ShippingDayFormData[];
    loadMappedCategoriesOnly?: boolean;
    matchProductsWithinMappedCategoriesOnly?: boolean;
}

const DAY_NAMES = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

interface FormErrors {
    name?: string;
    supplierName?: string;
    data?: string;
    jobConfiguration?: {
        filtersFilePath?: string;
        backupFormat?: string;
    };
    warehouses?: string;
}

interface SupplierData {
    useWarehouses: boolean;
    warehouses: string[];
    supplierConfiguration: Record<string, unknown>;
}

const SUPPORTED_FILTER_FILE_TYPES = [
    { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extensions: ['xlsx'] },
];

const isSupportedFilterFile = (file: File): boolean => {
    return SUPPORTED_FILTER_FILE_TYPES.some(type =>
        file.type === type.mime || type.extensions.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
    );
};

const getSupportedFilterFileTypesText = (): string => {
    return SUPPORTED_FILTER_FILE_TYPES.flatMap(type => type.extensions.map(ext => ext.toUpperCase())).join(', ');
};

const getConfigFieldsForSupplier = (supplierName: string): string[] => {
    const name = supplierName.toLowerCase();

    if (name.includes('elko')) {
        return ElkoConfiguration.configFields;
    } else if (name.includes('excel')) {
        return ExcelConfiguration.configFields;
    } else if (name.includes('itpartner') || name.includes('it partner')) {
        return ITPartnerConfiguration.configFields;
    } else if (name.includes('klavtorg')) {
        return KlavtorgConfiguration.configFields;
    } else if (name.includes('vtt')) {
        return VttConfiguration.configFields;
    } else if (name.includes('logic3') || name.includes('logic')) {
        return Logic3Configuration.configFields;
    } else if (name.includes('merlion')) {
        return MerlionConfiguration.configFields;
    } else if (name.includes('nag')) {
        return NagConfiguration.configFields;
    } else if (name.includes('nvprint') || name.includes('nv print')) {
        return NVPrintConfiguration.configFields;
    } else if (name.includes('ocs')) {
        return OCSConfiguration.configFields;
    } else if (name.includes('profiline')) {
        return ProfilineConfiguration.configFields;
    } else if (name.includes('ramis')) {
        return RamisConfiguration.configFields;
    } else if (name.includes('resurs') && name.includes('media')) {
        return ResursMediaConfiguration.configFields;
    } else if (name.includes('softtronik') || name.includes('soft tronik')) {
        return SoftTronikConfiguration.configFields;
    } else if (name.includes('bulat')) {
        return BulatConfiguration.configFields;
    } else if (name.includes('cet')) {
        return CETConfiguration.configFields;
    }

    return [];
};

const IntegrationEditModal: React.FC<IntegrationEditModalProps> = ({
    integration,
    currentUser,
    isOpen,
    onClose,
    onSave
}) => {
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'basic' | 'job'>('basic');
    const [formData, setFormData] = useState<FormData>({
        name: '',
        supplierName: '',
        data: '{}',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [cleaning, setCleaning] = useState<boolean>(false);

    // State for filter file uploads
    const [uploadingFilter, setUploadingFilter] = useState<boolean>(false);
    const [dragOver, setDragOver] = useState<boolean>(false);
    const filterFileInputRef = useRef<HTMLInputElement>(null);

    // State for supplier data and warehouses
    const [currentSupplierData, setCurrentSupplierData] = useState<SupplierData | null>(null);
    const [loadingSupplierData, setLoadingSupplierData] = useState<boolean>(false);

    // State for supplier configuration
    const [supplierConfiguration, setSupplierConfiguration] = useState<Record<string, unknown>>({});

    // Admin JSON editor state
    const [spoilerExpanded, setSpoilerExpanded] = useState(false);
    const [jsonData, setJsonData] = useState('');
    const [jsonError, setJsonError] = useState('');

    const isAdmin = currentUser?.roles?.includes('admin') || false;

    const handleClose = useCallback(() => {
        if (!saving && !cleaning) {
            onClose();
        }
    }, [cleaning, onClose, saving]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [handleClose, isOpen]);

    // Update JSON data when form data changes
    useEffect(() => {
        if (!spoilerExpanded && integration) {
            try {
                const parsedData = JSON.parse(formData.data);

                // Add supplier configuration
                Object.keys(supplierConfiguration).forEach(key => {
                    if (supplierConfiguration[key] !== undefined && supplierConfiguration[key] !== '') {
                        parsedData[key] = supplierConfiguration[key];
                    }
                });

                // Add warehouses if needed
                if (currentSupplierData?.useWarehouses && formData.selectedWarehouses && formData.selectedWarehouses.length > 0) {
                    parsedData.Warehouses = formData.selectedWarehouses;
                }

                parsedData.LoadMappedCategoriesOnly = formData.loadMappedCategoriesOnly ?? false;
                parsedData.MatchProductsWithinMappedCategoriesOnly = formData.matchProductsWithinMappedCategoriesOnly ?? false;

                setJsonData(JSON.stringify(parsedData, null, 2));
            } catch (error) {
                console.error('Error updating JSON data:', error);
            }
        }
    }, [formData, supplierConfiguration, currentSupplierData, spoilerExpanded, integration]);

    // Загрузка списка поставщиков
    const loadSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/Suppliers');
            if (!response.ok) {
                throw new Error('Failed to fetch suppliers');
            }
            const result = await response.json();
            setSuppliers(result.data);
        } catch (error) {
            console.error('Ошибка при загрузке поставщиков:', error);
            toast.error('Ошибка при загрузке поставщиков: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setLoading(false);
        }
    }, []);

    // Загружаем поставщиков при открытии модального окна
    useEffect(() => {
        if (isOpen) {
            loadSuppliers();
        }
    }, [isOpen, loadSuppliers]);

    // Загрузка данных поставщика при выборе
    const loadSupplierData = useCallback(async (supplierType: number) => {
        if (!supplierType) {
            setCurrentSupplierData(null);
            return;
        }

        try {
            setLoadingSupplierData(true);
            const response = await fetch(`/api/Suppliers/${supplierType}`);
            if (!response.ok) {
                throw new Error('Failed to fetch supplier data');
            }
            const supplierJson = await response.json();
            const supplier = supplierJson.data;

            // Parse supplier data
            let supplierData: SupplierData = { useWarehouses: false, warehouses: [], supplierConfiguration: {} };
            const supplierConfig: Record<string, unknown> = {};
            if (supplier.data) {
                try {
                    const parsed = JSON.parse(supplier.data);
                    supplierData = {
                        useWarehouses: parsed.useWarehouses || false,
                        warehouses: parsed.warehouses || [],
                        supplierConfiguration: parsed.supplierConfiguration || {}
                    };

                    // Extract supplier configuration fields as base settings for integration
                    const configFields = getConfigFieldsForSupplier(supplier.name);

                    configFields.forEach(field => {
                        if (parsed.supplierConfiguration[field] !== undefined) {
                            supplierConfig[field] = parsed.supplierConfiguration[field];
                        }
                    });

                    console.log('Parsed supplier data:', supplierData);
                    console.log('Loaded supplier configuration as base settings:', supplierConfig);
                } catch (error) {
                    console.error('Ошибка при разборе данных поставщика:', error);
                }
            }
            setCurrentSupplierData(supplierData);
            // Set supplier configuration as base settings for integration (only if not editing existing integration)
            if (!integration) {
                setSupplierConfiguration(supplierConfig);
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных поставщика:', error);
            toast.error('Ошибка при загрузке данных поставщика: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
            setCurrentSupplierData(null);
        } finally {
            setLoadingSupplierData(false);
        }
    }, [integration]);

    // Инициализация формы при открытии модального окна
    useEffect(() => {
        if (isOpen && integration) {
            // Parse JobConfiguration
            let jobConfiguration: IntegrationJobConfiguration | undefined;
            try {
                jobConfiguration = integration.jobConfiguration ?
                    JSON.parse(integration.jobConfiguration) as IntegrationJobConfiguration :
                    {
                        loadFiltersBeforePull: false,
                        filtersFilePath: '',
                        exportType: 'none',
                        exportFilePath: '',
                        backupEnabled: false,
                        backupFormat: 'yyyyMMddHHmmss'
                    };
            } catch (error) {
                console.error('Ошибка при разборе JobConfiguration:', error);
                jobConfiguration = {
                    loadFiltersBeforePull: false,
                    filtersFilePath: '',
                    exportType: 'none',
                    exportFilePath: '',
                    backupEnabled: false,
                    backupFormat: 'yyyyMMddHHmmss'
                };
            }

            // Parse existing warehouses from integration data
            let selectedWarehouses: string[] | undefined;
            let loadMappedCategoriesOnly = false;
            let matchProductsWithinMappedCategoriesOnly = false;
            const supplierConfig: Record<string, unknown> = {};
            try {
                const parsedData = JSON.parse(integration.data);
                selectedWarehouses = parsedData.Warehouses;
                loadMappedCategoriesOnly = parsedData.LoadMappedCategoriesOnly ?? parsedData.loadMappedCategoriesOnly ?? false;
                matchProductsWithinMappedCategoriesOnly = parsedData.MatchProductsWithinMappedCategoriesOnly
                    ?? parsedData.matchProductsWithinMappedCategoriesOnly
                    ?? false;

                // Get the appropriate configuration fields based on supplier name
                const configFields = getConfigFieldsForSupplier(integration.supplierName);

                configFields.forEach(field => {
                    if (parsedData[field] !== undefined) {
                        supplierConfig[field] = parsedData[field];
                    }
                });
            } catch (error) {
                console.error('Ошибка при разборе данных интеграции:', error);
            }

            // Load shipping days from integration
            const shippingDays: ShippingDayFormData[] = (integration.shippingDaysList || []).map(sd => ({
                id: sd.id,
                dayOfWeek: sd.dayOfWeek,
                cutoffTime: sd.cutoffTime || '00:00:00'
            }));

            setFormData({
                name: integration.name,
                supplierName: integration.supplierName,
                data: integration.data,
                jobConfiguration: jobConfiguration,
                selectedWarehouses: selectedWarehouses,
                stockLifeTimeHours: integration.stockLifeTimeHours ?? 48,
                deliveryTimeHours: integration.deliveryTimeHours ?? 0,
                saveExpectedToMainProduct: integration.saveExpectedToMainProduct ?? false,
                isStockQuantitySource: integration.isStockQuantitySource ?? false,
                shippingDays: shippingDays,
                loadMappedCategoriesOnly,
                matchProductsWithinMappedCategoriesOnly,
            });
            setSupplierConfiguration(supplierConfig);
            setJsonData(JSON.stringify(JSON.parse(integration.data), null, 2));
            setErrors({});

            // Load supplier data for the selected supplier
            const selectedSupplier = suppliers.find(s => s.name === integration.supplierName);
            if (selectedSupplier) {
                loadSupplierData(selectedSupplier.supplierType);
            }
        } else if (isOpen && !integration) {
            // Создание новой интеграции
            const defaultJobConfiguration: IntegrationJobConfiguration = {
                loadFiltersBeforePull: false,
                filtersFilePath: '',
                exportType: 'none',
                exportFilePath: '',
                backupEnabled: false,
                backupFormat: 'yyyyMMddHHmmss'
            };

            setFormData({
                name: '',
                supplierName: '',
                data: '{}',
                jobConfiguration: defaultJobConfiguration,
                loadMappedCategoriesOnly: false,
                matchProductsWithinMappedCategoriesOnly: false,
            });
            setErrors({});
        }
    }, [isOpen, integration, loadSupplierData, suppliers]);

    // Set default warehouse selection when supplier data is loaded
    useEffect(() => {
        if (currentSupplierData?.useWarehouses && currentSupplierData.warehouses.length > 0) {
            // If no warehouses are selected yet (null/undefined), select all by default
            setFormData(prev => {
                if (prev.selectedWarehouses === undefined) {
                    console.log('Setting default warehouses:', currentSupplierData.warehouses);
                    return {
                        ...prev,
                        selectedWarehouses: [...currentSupplierData.warehouses]
                    };
                }
                return prev;
            });
        }
    }, [currentSupplierData]);

    // Check if filters file exists
    const filtersFileExists = formData.jobConfiguration?.loadFiltersBeforePull && formData.jobConfiguration.filtersFilePath;

    const uploadFilterFile = async (file: File) => {
        if (!integration) return;

        setUploadingFilter(true);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            formDataUpload.append('integrationName', integration.name);

            const response = await fetch(`/api/Integrations/${integration.id}/filter-file`, {
                method: 'POST',
                body: formDataUpload,
            });

            if (!response.ok) {
                throw new Error('Failed to upload filter file');
            }

            const result = await response.json();
            const filterFileName = result.filePath || file.name;

            // Update the configuration to enable filters and set the file path
            handleJobConfigChange('loadFiltersBeforePull', true);
            handleJobConfigChange('filtersFilePath', filterFileName);

            toast.success('Файл фильтра загружен успешно');
            // Reset file input
            if (filterFileInputRef.current) {
                filterFileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Ошибка при загрузке файла фильтра:', error);
            toast.error('Ошибка при загрузке файла фильтра: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setUploadingFilter(false);
        }
    };

    const deleteFilterFile = async () => {
        if (!integration || !formData.jobConfiguration?.filtersFilePath) return;

        try {
            const response = await fetch(`/api/Integrations/${integration.id}/filter-file`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete filter file');
            }

            // Update the configuration to disable filters
            handleJobConfigChange('loadFiltersBeforePull', false);
            handleJobConfigChange('filtersFilePath', '');

            toast.success('Файл фильтра удален');
        } catch (error) {
            console.error('Ошибка при удалении файла фильтра:', error);
            toast.error('Ошибка при удалении файла фильтра: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Название интеграции обязательно для заполнения';
        } else if (formData.name.includes(' ')) {
            newErrors.name = 'Название интеграции не должно содержать пробелы';
        }

        if (!formData.supplierName?.trim()) {
            newErrors.supplierName = 'Поставщик обязателен для заполнения';
        }

        if (!formData.data?.trim()) {
            newErrors.data = 'Данные интеграции обязательны для заполнения';
        } else {
            try {
                JSON.parse(formData.data);
            } catch {
                newErrors.data = 'Данные должны быть в формате JSON';
            }
        }

        // Валидация складов - если поставщик использует склады, должен быть выбран хотя бы один
        if (currentSupplierData?.useWarehouses && (!formData.selectedWarehouses || formData.selectedWarehouses.length === 0)) {
            newErrors.warehouses = 'Необходимо выбрать хотя бы один склад для синхронизации';
        }

        // Валидация для фильтров - больше не нужна, т.к. файловая система сама указывает на наличие фильтра

        // Валидация для бэкапа
        if (formData.jobConfiguration?.backupEnabled && !formData.jobConfiguration.backupFormat.trim()) {
            newErrors.jobConfiguration = newErrors.jobConfiguration || {};
            newErrors.jobConfiguration.backupFormat = 'Формат бэкапа обязателен, если бэкап включен';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof FormData, value: string | number | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Очистка ошибки при изменении поля
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleJobConfigChange = (field: keyof IntegrationJobConfiguration, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            jobConfiguration: prev.jobConfiguration ? {
                ...prev.jobConfiguration,
                [field]: value
            } : prev.jobConfiguration
        }));

        // Очистка ошибки при изменении поля
        if (errors.jobConfiguration?.[field as keyof typeof errors.jobConfiguration]) {
            setErrors(prev => ({
                ...prev,
                jobConfiguration: prev.jobConfiguration ? {
                    ...prev.jobConfiguration,
                    [field]: undefined
                } : undefined
            }));
        }
    };

    const handleSupplierConfigChange = (field: string, value: string | number | unknown) => {
        setSupplierConfiguration(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const renderSupplierConfiguration = () => {
        if (!formData.supplierName) return null;

        const supplierName = formData.supplierName.toLowerCase();

        const commonProps = {
            configuration: supplierConfiguration,
            onChange: handleSupplierConfigChange,
            disabled: saving
        };

        console.log('Rendering supplier configuration for:', supplierName);
        console.log('Supplier configuration:', supplierConfiguration);

        if (supplierName.includes('elko')) {
            return <ElkoConfiguration {...commonProps} />;
        } else if (supplierName.includes('excel')) {
            return <ExcelConfiguration {...commonProps} />;
        } else if (supplierName.includes('itpartner') || supplierName.includes('it partner')) {
            return <ITPartnerConfiguration {...commonProps} />;
        } else if (supplierName.includes('klavtorg')) {
            return <KlavtorgConfiguration {...commonProps} />;
        } else if (supplierName.includes('vtt')) {
            return <VttConfiguration {...commonProps} />;
        } else if (supplierName.includes('logic3') || supplierName.includes('logic')) {
            return <Logic3Configuration {...commonProps} />;
        } else if (supplierName.includes('merlion')) {
            return <MerlionConfiguration {...commonProps} />;
        } else if (supplierName.includes('nag')) {
            return <NagConfiguration {...commonProps} />;
        } else if (supplierName.includes('nvprint') || supplierName.includes('nv print')) {
            return <NVPrintConfiguration {...commonProps} />;
        } else if (supplierName.includes('ocs')) {
            return <OCSConfiguration {...commonProps} />;
        } else if (supplierName.includes('profiline')) {
            return <ProfilineConfiguration {...commonProps} />;
        } else if (supplierName.includes('ramis')) {
            return <RamisConfiguration {...commonProps} />;
        } else if (supplierName.includes('resurs') && supplierName.includes('media')) {
            return <ResursMediaConfiguration {...commonProps} />;
        } else if (supplierName.includes('softtronik') || supplierName.includes('soft tronik')) {
            return <SoftTronikConfiguration {...commonProps} />;
        } else if (supplierName.includes('bulat')) {
            return <BulatConfiguration {...commonProps} />;
        } else if (supplierName.includes('cet')) {
            return <CETConfiguration {...commonProps} />;
        }

        return null;
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && !isCreateMode) {
            await uploadFilterFile(file);
        }
    };

    const handleDrop = async (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (isCreateMode || saving || uploadingFilter) return;

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            // Check file type
            if (isSupportedFilterFile(file)) {
                await uploadFilterFile(file);
            } else {
                toast.error(`Неподдерживаемый формат файла. Используйте ${getSupportedFilterFileTypesText()}.`);
            }
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        // Prevent saving if spoiler is expanded and there's a JSON error
        if (spoilerExpanded && jsonError) {
            toast.error('Неверный формат JSON. Исправьте ошибки перед сохранением.');
            return;
        }

        let dataToSave: string;

        // If admin has edited JSON and it's valid, use the JSON data
        if (isAdmin && spoilerExpanded && jsonData.trim() && !jsonError) {
            try {
                JSON.parse(jsonData); // Validate JSON
                dataToSave = jsonData;
            } catch {
                toast.error('Неверный формат JSON. Исправьте ошибки перед сохранением.');
                return;
            }
        } else {
            // Use the form data
            let integrationData = formData.data;
            try {
                const parsedData = JSON.parse(formData.data);

                // Add supplier configuration
                Object.keys(supplierConfiguration).forEach(key => {
                    if (supplierConfiguration[key] !== undefined && supplierConfiguration[key] !== '') {
                        parsedData[key] = supplierConfiguration[key];
                    }
                });

                // Add warehouses if needed
                if (currentSupplierData?.useWarehouses && formData.selectedWarehouses && formData.selectedWarehouses.length > 0) {
                    parsedData.Warehouses = formData.selectedWarehouses;
                }

                parsedData.LoadMappedCategoriesOnly = formData.loadMappedCategoriesOnly ?? false;
                parsedData.MatchProductsWithinMappedCategoriesOnly = formData.matchProductsWithinMappedCategoriesOnly ?? false;

                integrationData = JSON.stringify(parsedData);
            } catch (error) {
                console.error('Ошибка при обновлении данных интеграции:', error);
                toast.error('Ошибка при подготовке данных интеграции');
                return;
            }
            dataToSave = integrationData;
        }

        setSaving(true);
        try {
            // Serialize JobConfiguration
            let jobConfigurationJson: string | undefined;
            if (formData.jobConfiguration) {
                try {
                    jobConfigurationJson = JSON.stringify(formData.jobConfiguration);
                } catch (error) {
                    console.error('Ошибка при сериализации JobConfiguration:', error);
                    toast.error('Ошибка при подготовке настроек задачи');
                    return;
                }
            }

            const dataToSaveObj: CreateIntegrationRequest | UpdateIntegrationRequest = {
                name: formData.name,
                supplierName: formData.supplierName,
                data: dataToSave,
                ...(jobConfigurationJson ? { jobConfiguration: jobConfigurationJson } : {}),
                stockLifeTimeHours: formData.stockLifeTimeHours ?? 48,
                deliveryTimeHours: formData.deliveryTimeHours ?? 0,
                saveExpectedToMainProduct: formData.saveExpectedToMainProduct ?? false,
                isStockQuantitySource: formData.isStockQuantitySource ?? false,
                shippingDays: formData.shippingDays
            };

            await onSave(dataToSaveObj);
        } catch (error) {
            console.error('Ошибка при сохранении интеграции:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleCleanupProducts = async () => {
        if (!integration?.id) {
            return;
        }

        const confirmCleanup = window.confirm(
            'Очистить данные товаров этой интеграции? Это действие нельзя отменить.'
        );
        if (!confirmCleanup) {
            return;
        }

        setCleaning(true);
        try {
            const response = await fetch(`/api/IntegrationCleanup/products/${integration.id}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                let errorMessage = 'Failed to clean integration data';
                try {
                    const errorJson = await response.json();
                    errorMessage = errorJson?.error ?? errorMessage;
                } catch {
                    // ignore parse error
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            const deletedProducts =
                result?.data?.Deleted?.Products ??
                result?.data?.deleted?.products ??
                result?.Deleted?.Products ??
                result?.deleted?.products ??
                0;

            toast.success(`Очистка завершена. Удалено товаров: ${deletedProducts}`);
        } catch (error) {
            console.error('Cleanup error:', error);
            toast.error('Cleanup failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setCleaning(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    const isCreateMode = !integration;
    const title = isCreateMode ? 'Создание интеграции' : 'Редактирование интеграции';
    const subtitle = isCreateMode ? 'Добавление новой интеграции в систему' : 'Управление настройками интеграции';
    const saveButtonText = isCreateMode ? 'Создать интеграцию' : 'Сохранить изменения';

    return (
        <div className="modal-backdrop-custom">
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="client-avatar mr-3">
                                {formData.name?.charAt(0).toUpperCase() || (isCreateMode ? 'И' : 'И')}
                            </div>
                            <div>
                                <h3 className="modal-title-custom">{title}</h3>
                                <p className="modal-subtitle-custom">{subtitle}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={handleClose}
                            disabled={saving || cleaning}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        {/* Tab Navigation */}
                        <div className="tab-navigation">
                            <div className="tab-header d-flex">
                                <button
                                    type="button"
                                    className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('basic')}
                                    disabled={saving}
                                >
                                    <span className="tab-icon">📋</span>
                                    Основные настройки
                                </button>
                                <button
                                    type="button"
                                    className={`tab-button ${activeTab === 'job' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('job')}
                                    disabled={saving}
                                >
                                    <span className="tab-icon">⚙️</span>
                                    Настройки экспорта
                                </button>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'basic' && (
                            <div className="form-section">
                                <div className="form-grid">
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">
                                            <span className="label-icon">🔗</span>
                                            Название интеграции
                                        </label>
                                        <div className="input-group-custom">
                                            <input
                                                type="text"
                                                className="form-input-custom"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                placeholder="Введите название интеграции"
                                                disabled={saving}
                                            />
                                        </div>
                                        {errors.name && <div className="form-error">{errors.name}</div>}
                                    </div>

                                    <div className="form-group-custom">
                                        <label className="form-label-custom">
                                            <span className="label-icon">🏭</span>
                                            Поставщик
                                        </label>
                                        <div className="input-group-custom">
                                            <select
                                                className="form-select-custom"
                                                value={formData.supplierName}
                                                onChange={(e) => {
                                                    const selectedSupplier = suppliers.find(s => s.name === e.target.value);
                                                    handleInputChange('supplierName', e.target.value);
                                                    if (selectedSupplier) {
                                                        loadSupplierData(selectedSupplier.supplierType);
                                                    } else {
                                                        setCurrentSupplierData(null);
                                                    }
                                                }}
                                                disabled={saving || loading}
                                            >
                                                <option value="">Выберите поставщика</option>
                                                {suppliers.map(supplier => (
                                                    <option key={supplier.supplierType} value={supplier.name}>
                                                        {supplier.name} (Type: {supplier.supplierType})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {errors.supplierName && <div className="form-error">{errors.supplierName}</div>}
                                    </div>

                                    {/* Warehouse Selection */}
                                    {currentSupplierData?.useWarehouses && currentSupplierData.warehouses.length > 0 && (
                                        <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                            <label className="form-label-custom">
                                                <span className="label-icon">🏢</span>
                                                Склады для синхронизации
                                            </label>
                                            {loadingSupplierData ? (
                                                <div className="d-flex align-items-center p-3">
                                                    <span className="btn-spinner mr-2"></span>
                                                    <span>Загрузка складов...</span>
                                                </div>
                                            ) : (
                                                <div className="warehouse-selection">
                                                    <div className="warehouse-grid">
                                                        {currentSupplierData!.warehouses.map(warehouse => (
                                                            <div key={warehouse} className="form-check-custom warehouse-item">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    checked={formData.selectedWarehouses?.includes(warehouse) || false}
                                                                    onChange={(e) => {
                                                                        const isChecked = e.target.checked;
                                                                        setFormData(prev => {
                                                                            const currentWarehouses = prev.selectedWarehouses || [];
                                                                            if (isChecked) {
                                                                                return {
                                                                                    ...prev,
                                                                                    selectedWarehouses: [...currentWarehouses, warehouse]
                                                                                };
                                                                            } else {
                                                                                return {
                                                                                    ...prev,
                                                                                    selectedWarehouses: currentWarehouses.filter(w => w !== warehouse)
                                                                                };
                                                                            }
                                                                        });
                                                                    }}
                                                                    disabled={saving}
                                                                    id={`warehouse-${warehouse}`}
                                                                />
                                                                <label className="form-label-custom" htmlFor={`warehouse-${warehouse}`}>
                                                                    {warehouse}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {errors.warehouses && <div className="form-error">{errors.warehouses}</div>}
                                        </div>
                                    )}

                                    {/* Supplier Configuration */}
                                    {renderSupplierConfiguration()}

                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="label-icon">🔀</span>
                                            Настройки категорий
                                        </label>
                                        <div className="delivery-settings-grid">
                                            <div className="form-check-custom">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={formData.loadMappedCategoriesOnly ?? false}
                                                    onChange={(e) => handleInputChange('loadMappedCategoriesOnly', e.target.checked)}
                                                    disabled={saving}
                                                    id="load-mapped-categories-only"
                                                />
                                                <label className="form-label-custom" htmlFor="load-mapped-categories-only">
                                                    Подгружать сопоставленные
                                                </label>
                                            </div>

                                            <div className="form-check-custom">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={formData.matchProductsWithinMappedCategoriesOnly ?? false}
                                                    onChange={(e) => handleInputChange('matchProductsWithinMappedCategoriesOnly', e.target.checked)}
                                                    disabled={saving}
                                                    id="match-products-within-mapped-categories-only"
                                                />
                                                <label className="form-label-custom" htmlFor="match-products-within-mapped-categories-only">
                                                    Сопоставлять только в категориях
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delivery Settings */}
                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="label-icon">🚚</span>
                                            Настройки доставки
                                        </label>
                                        <div className="delivery-settings-grid">
                                            <div className="form-group-custom">
                                                <label className="form-label-custom">
                                                    <span className="label-icon">⏱️</span>
                                                    Срок жизни прайса (часов)
                                                </label>
                                                <div className="input-group-custom">
                                                    <input
                                                        type="number"
                                                        className="form-input-custom"
                                                        value={formData.stockLifeTimeHours ?? 48}
                                                        onChange={(e) => handleInputChange('stockLifeTimeHours', parseInt(e.target.value) || 48)}
                                                        min={0}
                                                        max={8760}
                                                        disabled={saving}
                                                        placeholder="48"
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group-custom">
                                                <label className="form-label-custom">
                                                    <span className="label-icon">📦</span>
                                                    Срок поставки (часов)
                                                </label>
                                                <div className="input-group-custom">
                                                    <input
                                                        type="number"
                                                        className="form-input-custom"
                                                        value={formData.deliveryTimeHours ?? 0}
                                                        onChange={(e) => handleInputChange('deliveryTimeHours', parseInt(e.target.value) || 0)}
                                                        min={0}
                                                        max={8760}
                                                        disabled={saving}
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-check-custom">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={formData.saveExpectedToMainProduct ?? false}
                                                    onChange={(e) => handleInputChange('saveExpectedToMainProduct', e.target.checked)}
                                                    disabled={saving}
                                                    id="save-expected-to-main-product"
                                                />
                                                <label className="form-label-custom" htmlFor="save-expected-to-main-product">
                                                    Сохранять ожидаемое в итоговой НЛ
                                                </label>
                                            </div>

                                            <div className="form-check-custom">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={formData.isStockQuantitySource ?? false}
                                                    onChange={(e) => handleInputChange('isStockQuantitySource', e.target.checked)}
                                                    disabled={saving}
                                                    id="is-stock-quantity-source"
                                                />
                                                <label className="form-label-custom" htmlFor="is-stock-quantity-source">
                                                    Источник наличия для НЛ
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Дни отгрузки */}
                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="label-icon">📅</span>
                                            Дни отгрузки
                                        </label>
                                        <div className="shipping-days-grid">
                                            {DAY_NAMES.map((dayName, dayIndex) => {
                                                const existingDay = formData.shippingDays?.find(sd => sd.dayOfWeek === dayIndex);
                                                const isSelected = !!existingDay;
                                                
                                                return (
                                                    <div key={dayIndex} className="shipping-day-item">
                                                        <div className="form-check-custom">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        // Add day with default cutoff time
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            shippingDays: [
                                                                                ...(prev.shippingDays || []),
                                                                                { dayOfWeek: dayIndex, cutoffTime: '18:00:00' }
                                                                            ]
                                                                        }));
                                                                    } else {
                                                                        // Remove day
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            shippingDays: (prev.shippingDays || []).filter(sd => sd.dayOfWeek !== dayIndex)
                                                                        }));
                                                                    }
                                                                }}
                                                                disabled={saving}
                                                                id={`shipping-day-${dayIndex}`}
                                                            />
                                                            <label className="form-label-custom" htmlFor={`shipping-day-${dayIndex}`}>
                                                                {dayName}
                                                            </label>
                                                        </div>
                                                        {isSelected && (
                                                            <div className="cutoff-time-input">
                                                                <input
                                                                    type="time"
                                                                    className="form-input-custom"
                                                                    value={existingDay?.cutoffTime?.substring(0, 5) || '18:00'}
                                                                    onChange={(e) => {
                                                                        const newTime = e.target.value + ':00';
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            shippingDays: (prev.shippingDays || []).map(sd => 
                                                                                sd.dayOfWeek === dayIndex 
                                                                                    ? { ...sd, cutoffTime: newTime }
                                                                                    : sd
                                                                            )
                                                                        }));
                                                                    }}
                                                                    disabled={saving}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {errors.data && (
                                        <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                            <div className="form-error">{errors.data}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'job' && formData.jobConfiguration && (
                            <div className="form-section">
                                <div className="form-grid">
                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="label-icon">🔍</span>
                                            Файл фильтров
                                        </label>
                                        {filtersFileExists ? (
                                            // File exists - show filename with delete button
                                            <div className="d-flex align-items-center gap-3 p-3 border rounded">
                                                <div className="flex-grow-1">
                                                    <span className="text-success mr-2">✓</span>
                                                    <span className="fw-medium">{formData.jobConfiguration.filtersFilePath}</span>
                                                    <div className="text-muted small">Фильтры активны</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn-secondary-custom btn-sm"
                                                    onClick={deleteFilterFile}
                                                    disabled={saving}
                                                >
                                                    <span className="btn-icon">🗑️</span>
                                                    Удалить
                                                </button>
                                            </div>
                                        ) : (
                                            // No file - show drag-and-drop upload area
                                            <div className="file-upload-area">
                                                <input
                                                    type="file"
                                                    ref={filterFileInputRef}
                                                    className="file-input-hidden"
                                                    onChange={handleFileSelect}
                                                    accept=".xlsx"
                                                    disabled={saving || uploadingFilter || isCreateMode}
                                                    id="filter-file-input"
                                                />
                                                <label
                                                    htmlFor="filter-file-input"
                                                    className={`file-upload-zone ${dragOver ? 'drag-over' : ''}`}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragEnter={handleDragEnter}
                                                    onDragLeave={handleDragLeave}
                                                >
                                                    {uploadingFilter ? (
                                                        <div className="upload-progress">
                                                            <span className="btn-spinner mr-2"></span>
                                                            <span>Загрузка файла...</span>
                                                        </div>
                                                    ) : (
                                                        <div className="upload-placeholder">
                                                            <div className="upload-text">
                                                                Перетащите файл сюда или <span className="upload-link">выберите файл</span>
                                                            </div>
                                                            <div className="upload-hint">
                                                                Поддерживаемые форматы: XLSX
                                                            </div>
                                                        </div>
                                                    )}
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="label-icon">📁</span>
                                            Путь к файлу экспорта
                                        </label>
                                        <div className="input-group-custom">
                                            <input
                                                type="text"
                                                className="form-input-custom"
                                                value={integration?.calculatedExportPath || 'Путь будет сгенерирован после сохранения'}
                                                readOnly
                                                disabled={saving}
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    color: '#6c757d',
                                                    cursor: 'not-allowed'
                                                }}
                                            />
                                        </div>
                                        <div className="text-muted small mt-1">
                                            Путь формируется автоматически как: Имя клиента/Имя поставщика/Имя интеграции/price.xlsx
                                        </div>
                                    </div>

                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <div className="form-check-custom mb-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={formData.jobConfiguration.backupEnabled}
                                                onChange={(e) => handleJobConfigChange('backupEnabled', e.target.checked)}
                                                disabled={saving}
                                                id="backupEnabledCheck"
                                            />
                                            <label className="form-label-custom" htmlFor="backupEnabledCheck">
                                                Бэкап включен
                                            </label>
                                        </div>
                                        <div className="input-group-custom">
                                            <input
                                                type="text"
                                                className="form-input-custom"
                                                value={formData.jobConfiguration.backupFormat}
                                                onChange={(e) => handleJobConfigChange('backupFormat', e.target.value)}
                                                placeholder="Формат имени файла, например: yyyyMMddHHmmss"
                                                disabled={saving || !formData.jobConfiguration.backupEnabled}
                                                style={{
                                                    opacity: formData.jobConfiguration.backupEnabled ? 1 : 0.5,
                                                    cursor: formData.jobConfiguration.backupEnabled ? 'text' : 'not-allowed'
                                                }}
                                            />
                                        </div>
                                        {errors.jobConfiguration?.backupFormat && <div className="form-error">{errors.jobConfiguration.backupFormat}</div>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Admin JSON Editor */}
                        {isAdmin && (
                            <div className="spoiler-container">
                                <button
                                    type="button"
                                    className="spoiler-header"
                                    onClick={() => setSpoilerExpanded(!spoilerExpanded)}
                                    disabled={saving}
                                >
                                    <div className="spoiler-title">
                                        <span className="warning-icon">⚠️</span>
                                        Расширенное редактирование JSON (только для администраторов)
                                        <svg
                                            className={`spoiler-icon ${spoilerExpanded ? 'rotated' : ''}`}
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <polyline points="6,9 12,15 18,9"/>
                                        </svg>
                                    </div>
                                </button>
                                <div className={`spoiler-content ${spoilerExpanded ? 'expanded' : ''}`}>
                                    <div className="spoiler-warning">
                                        <span className="warning-icon">⚠️</span>
                                        <strong>Внимание:</strong> Это расширенное редактирование позволяет напрямую изменять JSON данные интеграции.
                                        Неправильные изменения могут привести к неработоспособности интеграций. Используйте с осторожностью!
                                    </div>
                                    <div>
                                        <label className="form-label-custom">
                                            <span className="label-icon">📄</span>
                                            JSON данные интеграции
                                        </label>
                                        <textarea
                                            className="form-input-custom"
                                            value={jsonData}
                                            onChange={(e) => {
                                                setJsonData(e.target.value);
                                                try {
                                                    JSON.parse(e.target.value);
                                                    setJsonError('');
                                                } catch {
                                                    setJsonError('Неверный формат JSON');
                                                }
                                            }}
                                            placeholder="Введите JSON данные..."
                                            rows={10}
                                            disabled={saving}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                                lineHeight: '1.4'
                                            }}
                                        />
                                        {jsonError && <div className="form-error">{jsonError}</div>}
                                        <div className="form-help-text">
                                            Изменения в этом поле будут сохранены напрямую в базу данных.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer-custom">
                        {!isCreateMode && (
                            <button
                                type="button"
                                className="btn-secondary-custom"
                                onClick={handleCleanupProducts}
                                disabled={saving || cleaning}
                                style={{
                                    backgroundColor: '#c62828',
                                    borderColor: '#b71c1c',
                                    color: '#fff'
                                }}
                            >
                                {cleaning ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        {'Очистка...'}
                                    </>
                                ) : (
                                    <>
                                        <span className="btn-icon">🗑️</span>
                                        {'Очистить данные'}
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={handleClose}
                            disabled={saving || cleaning}
                        >
                            <span className="btn-icon">✕</span>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleSave}
                            disabled={saving || cleaning}
                        >
                            {saving ? (
                                <>
                                    <span className="btn-spinner"></span>
                                    {isCreateMode ? 'Создание...' : 'Сохранение...'}
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon">✓</span>
                                    {saveButtonText}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationEditModal;
